import React, { useState } from "react";
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, FormControl, InputLabel, Select, MenuItem,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
    IconButton, Snackbar, Alert
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";

import {
    useGetProductDetailQuery,
    useAddProductPricePackMutation,
    useUpdateProductPricePackMutation,
    useDeleteProductPricePackMutation,
} from "../../features/admin/productApi.ts";

interface Props {
    productId: number | null;
}

const PricePackManager: React.FC<Props> = ({ productId }) => {

    const { data: packsData, refetch } = useGetProductDetailQuery(
        { product_id: productId as number },
        { skip: !productId }
    );
    const [addPricePack] = useAddProductPricePackMutation();
    const [updatePricePack] = useUpdateProductPricePackMutation();
    const [deletePricePack] = useDeleteProductPricePackMutation();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPack, setEditingPack] = useState<any>(null);
    const [form, setForm] = useState<any>({
        unit: "kg",
        quantity: 1,
        product_size: "",
        price: 0,
        retail_price: 0,
        discount_type: 0,
        discount: 0,
    });

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const pricePacks: any[] = Array.isArray((packsData as any)?.prices)
        ? (packsData as any).prices
        : [];

    console.log('price packs=>', packsData);

    const handleOpen = (pack?: any) => {
        if (pack) {
            setEditingPack(pack);
            setForm(pack);
        } else {
            setEditingPack(null);
            setForm({ unit: "kg", quantity: 1, product_size: "", price: 0, retail_price: 0, discount_type: 0, discount: 0 });
        }
        setDialogOpen(true);
    };

    const handleClose = () => setDialogOpen(false);

    const handleChange = (field: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        try {
            if (!productId) return;
            const payload = { product_id: productId, ...form, ...(editingPack && { id: editingPack.id }) };

            let result;
            if (editingPack) {
                result = await updatePricePack(payload).unwrap();
            } else {
                result = await addPricePack(payload).unwrap();
            }

            setSnackbar({ open: true, message: result.message, severity: "success" });
            setDialogOpen(false);
            refetch();
        } catch (err: any) {
            setSnackbar({ open: true, message: err?.data?.message || "Error saving pack", severity: "error" });
        }
    };

    const handleDelete = async (id: number) => {
        if (!productId) return;
        if (!window.confirm("Delete this price pack?")) return;
        try {
            const result = await deletePricePack({ product_id: productId, id }).unwrap();
            setSnackbar({ open: true, message: result.message, severity: "success" });
            refetch();
        } catch (err: any) {
            setSnackbar({ open: true, message: err?.data?.message || "Delete failed", severity: "error" });
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Button variant="outlined" startIcon={<Add />} onClick={() => handleOpen()}>
                Add Additional Price
            </Button>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small"> 
                    <TableHead>
                        <TableRow>
                            <TableCell>S.No</TableCell>
                            <TableCell>Unit</TableCell>
                            <TableCell>Quantity</TableCell>
                            <TableCell>Size</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Retail Price</TableCell>
                            <TableCell>Discount</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pricePacks?.map((pack: any, index: any) => (
                            <TableRow key={pack.id}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{pack.unit}</TableCell>
                                <TableCell>{pack.quantity}</TableCell>
                                <TableCell>{pack.product_size}</TableCell>
                                <TableCell>{pack.price}</TableCell>
                                <TableCell>{pack.retail_price}</TableCell>
                                <TableCell>{pack.discount_type === 1 ? `${pack.discount}%` : pack.discount}</TableCell>
                                <TableCell align="center">
                                    <IconButton onClick={() => handleOpen(pack)}><Edit /></IconButton>
                                    <IconButton color="error" onClick={() => handleDelete(pack.id)}><Delete /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>


            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPack ? "Edit Price Pack" : "Add Price Pack"}</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <TextField label="Unit" value={form.unit} onChange={(e) => handleChange("unit", e.target.value)} />
                    <TextField label="Quantity" type="number" value={form.quantity} onChange={(e) => handleChange("quantity", Number(e.target.value))} />
                    <TextField label="Product Size" value={form.product_size} onChange={(e) => handleChange("product_size", e.target.value)} />
                    <TextField label="Price" type="number" value={form.price} onChange={(e) => handleChange("price", Number(e.target.value))} />
                    <TextField label="Retail Price" type="number" value={form.retail_price} onChange={(e) => handleChange("retail_price", Number(e.target.value))} />
                    <FormControl>
                        <InputLabel>Discount Type</InputLabel>
                        <Select value={form.discount_type} onChange={(e) => handleChange("discount_type", Number(e.target.value))}>
                            <MenuItem value={0}>No Discount</MenuItem>
                            <MenuItem value={1}>Percentage</MenuItem>
                            <MenuItem value={2}>Fixed</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Discount" type="number" value={form.discount} onChange={(e) => handleChange("discount", Number(e.target.value))} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>{editingPack ? "Update" : "Save"}</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity as any}>{snackbar.message}</Alert>
            </Snackbar> 
        </Box>
    );
};

export default PricePackManager;

